import axios from 'axios';
import { config } from '../config';

const API_URL = config.apiUrl;

const api = axios.create({
  baseURL: API_URL,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const invoiceService = {
  getAll: () => api.get('/invoices'),
  getById: (id: number) => api.get(`/invoices/${id}`),
  create: (data: any) => api.post('/invoices', data),
  update: (id: number, data: any) => api.put(`/invoices/${id}`, data),
  delete: (id: number) => api.delete(`/invoices/${id}`),
  importCsv: (clientId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/invoices/import/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAllByClient: (clientId: number) => api.delete(`/invoices/client/${clientId}/all`),
  deleteMultiple: (ids: number[]) => api.post('/invoices/delete-multiple', { ids }),
};

export const commentService = {
  getByInvoice: (invoiceId: number) => api.get(`/comments/invoice/${invoiceId}`),
  create: (data: { invoiceId: number; comment: string }) => api.post('/comments', data),
  update: (id: number, comment: string) => api.put(`/comments/${id}`, { comment }),
  delete: (id: number) => api.delete(`/comments/${id}`),
};

export const clientService = {
  getAll: () => api.get('/clients'),
  getById: (id: number) => api.get(`/clients/${id}`),
  getInvoices: (id: number) => api.get(`/clients/${id}/invoices`),
  create: (data: any) => api.post('/clients', data),
  update: (id: number, data: any) => api.put(`/clients/${id}`, data),
};

export const planService = {
  getAll: () => api.get('/plans'),
  create: (data: any) => api.post('/plans', data),
  update: (id: number, data: any) => api.put(`/plans/${id}`, data),
  delete: (id: number) => api.delete(`/plans/${id}`),
  import: (plans: any[]) => api.post('/plans/import', { plans }),
};

export const userService = {
  getAll: () => api.get('/users'),
  create: (data: any) => api.post('/users', data),
  update: (id: number, data: any) => api.put(`/users/${id}`, data),
  updatePassword: (id: number, password: string) => api.put(`/users/${id}/password`, { password }),
  delete: (id: number) => api.delete(`/users/${id}`),
};

export const documentService = {
  getMyDocuments: () => api.get('/documents/my-documents'),
  getByClient: (clientId: number) => api.get(`/documents/client/${clientId}`),
  upload: (clientId: number, file: File, documentDate?: string, description?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    if (documentDate) formData.append('documentDate', documentDate);
    if (description) formData.append('description', description);
    return api.post(`/documents/client/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  download: (id: number) => api.get(`/documents/download/${id}`, { responseType: 'blob' }),
  preview: (id: number) => api.get(`/documents/preview/${id}`, { responseType: 'blob' }),
  getPreviewUrl: (id: number) => `${API_URL}/documents/preview/${id}`,
  delete: (id: number) => api.delete(`/documents/${id}`),
};

export const reportService = {
  analyzePdf: (file: File) => {
    const formData = new FormData();
    formData.append('pdf', file);
    return api.post('/reports/analyze-pdf', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  analyzePdfs: (files: File[]) => {
    const formData = new FormData();
    files.forEach(file => formData.append('pdfs', file));
    return api.post('/reports/analyze-pdfs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  save: (clientId: number, file: File, data: {
    reportType?: 'facturado' | 'aging' | 'cheques';
    dateFrom?: string;
    dateTo?: string;
    numFacturas?: number;
    totalFacturado?: number;
    totalDeducible?: number;
    totalCobrado?: number;
    totalNeto?: number;
    plansData?: any;
  }) => {
    const formData = new FormData();
    formData.append('pdf', file);
    if (data.reportType) formData.append('reportType', data.reportType);
    if (data.dateFrom) formData.append('dateFrom', data.dateFrom);
    if (data.dateTo) formData.append('dateTo', data.dateTo);
    if (data.numFacturas) formData.append('numFacturas', data.numFacturas.toString());
    if (data.totalFacturado) formData.append('totalFacturado', data.totalFacturado.toString());
    if (data.totalDeducible) formData.append('totalDeducible', data.totalDeducible.toString());
    if (data.totalCobrado) formData.append('totalCobrado', data.totalCobrado.toString());
    if (data.totalNeto) formData.append('totalNeto', data.totalNeto.toString());
    if (data.plansData) formData.append('plansData', JSON.stringify(data.plansData));
    return api.post(`/reports/save/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  getByClient: (clientId: number) => api.get(`/reports/client/${clientId}`),
  getMyReports: () => api.get('/reports/my-reports'),
  download: (id: number) => api.get(`/reports/download/${id}`, { responseType: 'blob' }),
  delete: (id: number) => api.delete(`/reports/${id}`),
};

export const clientEmployeeService = {
  getAll: () => api.get('/client-employees'),
  create: (data: { email: string; password: string; firstName: string; lastName: string; clientRole: string }) => 
    api.post('/client-employees', data),
  update: (id: number, data: { firstName?: string; lastName?: string; clientRole?: string; active?: boolean; password?: string }) => 
    api.put(`/client-employees/${id}`, data),
  delete: (id: number) => api.delete(`/client-employees/${id}`),
};

export const taskService = {
  getAll: (params?: { status?: string; priority?: string; assigned_to?: number }) => 
    api.get('/tasks', { params }),
  getById: (id: number) => api.get(`/tasks/${id}`),
  create: (data: { title: string; description?: string; priority?: string; due_date?: string; assigned_to?: number }) => 
    api.post('/tasks', data),
  update: (id: number, data: { title?: string; description?: string; priority?: string; status?: string; due_date?: string; assigned_to?: number }) => 
    api.put(`/tasks/${id}`, data),
  delete: (id: number) => api.delete(`/tasks/${id}`),
  getAdmins: () => api.get('/tasks/users/admins'),
};

export const clientLogService = {
  getByClient: (clientId: number) => api.get(`/client-logs/client/${clientId}`),
  create: (data: { client_id: number; facturado_desde?: string; facturado_hasta?: string; facturador?: string; notas?: string }) =>
    api.post('/client-logs', data),
  update: (id: number, data: { notas?: string }) => api.put(`/client-logs/${id}`, data),
  delete: (id: number) => api.delete(`/client-logs/${id}`),
};

export const chequeService = {
  getAll: () => api.get('/cheques'),
  getByClient: (clientId: number) => api.get(`/cheques/client/${clientId}`),
  getMyCheques: () => api.get('/cheques/my-cheques'),
  getById: (id: number) => api.get(`/cheques/${id}`),
  create: (data: any) => api.post('/cheques', data),
  update: (id: number, data: any) => api.put(`/cheques/${id}`, data),
  delete: (id: number) => api.delete(`/cheques/${id}`),
  addLog: (id: number, data: { nota: string; facturador?: string }) => api.post(`/cheques/${id}/log`, data),
  addFactura: (id: number, data: { factura_numero: string; notas?: string; facturador?: string }) => 
    api.post(`/cheques/${id}/factura`, data),
  deleteFactura: (facturaId: number) => api.delete(`/cheques/factura/${facturaId}`),
  addNota: (id: number, data: { nota: string }) => api.post(`/cheques/${id}/nota`, data),
  deleteNota: (notaId: number) => api.delete(`/cheques/nota/${notaId}`),
  importCsv: (clientId: number, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/cheques/import/${clientId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  deleteAllByClient: (clientId: number) => api.delete(`/cheques/client/${clientId}/all`),
};

export const dashboardService = {
  getStats: () => api.get('/dashboard'),
  getClientStats: () => api.get('/dashboard/client'),
};

export default api;
